/**
 * The chat socket (web-plan.md F1) — the web port of `net/ws.rs`.
 *
 * Shape preserved from the desktop:
 * - one supervisor owns one connection at a time and serialises commands, so
 *   "stop then start" cannot interleave;
 * - authorization is the socket's first frame (`{"t":"auth","jwt"}`) — that
 *   design existed *for* this client, a browser cannot set the header;
 * - the backoff ladder is 500 ms → 1 s → 2 s → 4 s → 8 s → 8 s…, doubling
 *   after each sleep, reset **only** by a session that actually reached
 *   `ready` (a socket refused right after auth stays on the ladder);
 * - an `error` frame before `ready` is a verdict on the token: terminal, no
 *   retry — the caller logs out (unless the password-change window is armed,
 *   R44, in which case it redials with the fresh JWT, or the verdict is the
 *   address gate's, docs/fast-start.md D1089, and it shows the address card);
 * - `salvage`: a frame that fails to parse is only ever rescued when its `t`
 *   is `message`/`message_edited` — a placeholder row with
 *   `service: {kind:"unknown"}` keeps the seq visible (chat-api.md §7);
 *   everything else is state the next sync re-establishes.
 *
 * **Deliberate divergence — the liveness deadline.** The desktop drops a
 * connection silent for 95 s (three missed 30 s server pings). The gateway's
 * keepalive is a *protocol* ping, and browsers answer it invisibly — no
 * `message` event fires — so "no frames in for 95 s" cannot be measured here
 * and a naive port would kill every healthy quiet socket. Instead the web
 * client sends the app-level `{"t":"pong"}` every 30 s and watches
 * `bufferedAmount`: a black-holed route stops draining the send buffer, and
 * two consecutive keepalives stuck in it is the web-reachable version of the
 * same fact. `navigator.onLine` flips and a `reconnect()` from whoever knows
 * the path moved cover the rest.
 */
import type { AuthFailure, ServerEvent } from "../../gen/dto";
import { SERVER_EVENT_TAGS } from "../../gen/dto";

export const MIN_BACKOFF_MS = 500;
export const MAX_BACKOFF_MS = 8_000;
export const KEEPALIVE_MS = 30_000;

export type SocketEvent =
  | { t: "status"; connected: boolean }
  | { t: "ready"; user_id: number }
  | { t: "event"; event: ServerEvent }
  | { t: "auth_rejected"; message: string; reason?: AuthFailure };

type Listener = (e: SocketEvent) => void;

const KNOWN_TAGS: ReadonlySet<string> = new Set(SERVER_EVENT_TAGS);

export class ChatSocket {
  private listener: Listener;
  private ws: WebSocket | null = null;
  private started: { url: string; jwt: string } | null = null;
  private connected = false;
  private backoffMs = MIN_BACKOFF_MS;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private keepaliveTimer: ReturnType<typeof setInterval> | null = null;
  private stuckKeepalives = 0;
  /** Bumped on every (re)start; stale socket callbacks check it and die. */
  private generation = 0;
  private readonly onOnline = () => this.reconnect();

  constructor(listener: Listener) {
    this.listener = listener;
    // Whoever knows the path moved says so — don't wait out any deadline.
    window.addEventListener("online", this.onOnline);
  }

  dispose(): void {
    this.stop();
    window.removeEventListener("online", this.onOnline);
  }

  isConnected(): boolean {
    return this.connected;
  }

  start(url: string, jwt: string): void {
    this.started = { url, jwt };
    this.backoffMs = MIN_BACKOFF_MS;
    this.dial();
  }

  /** For the case no ladder can see: the connection is fine as far as the
   * kernel knows, it just no longer goes anywhere. */
  reconnect(): void {
    if (!this.started) return; // a stopped socket cannot be woken by a stray reconnect
    this.dial();
  }

  stop(): void {
    this.started = null;
    this.teardown();
    this.setConnected(false);
  }

  /** Fire-and-forget: a lost typing frame is worth exactly nothing. */
  send(frame: object): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(frame));
    }
  }

  sendTyping(channelId: number): void {
    this.send({ t: "typing", channel_id: channelId });
  }

  /** The gate (connected && presence.enabled && a transition) is entirely the
   * caller's — an older gateway drops the frame in silence and there is
   * nothing an error would mean. */
  sendActivity(idle: boolean): void {
    this.send({ t: "activity", idle });
  }

  // -------------------------------------------------------------------------

  private dial(): void {
    if (!this.started) return;
    this.teardown();
    const gen = ++this.generation;
    const { url, jwt } = this.started;

    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      this.scheduleRetry(String(e));
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      if (gen !== this.generation) return;
      // The first frame, before anything else.
      ws.send(JSON.stringify({ t: "auth", jwt }));
      this.armKeepalive(gen);
    };

    ws.onmessage = (ev) => {
      if (gen !== this.generation) return;
      if (typeof ev.data !== "string") return; // binary is not this contract
      this.dispatch(ev.data);
    };

    ws.onclose = () => {
      if (gen !== this.generation) return;
      this.scheduleRetry("chat socket closed");
    };

    ws.onerror = () => {
      // onclose follows; the retry is scheduled there.
    };
  }

  private dispatch(text: string): void {
    let raw: unknown;
    try {
      raw = JSON.parse(text);
    } catch {
      return; // not even JSON: nothing to salvage
    }
    const tag = (raw as { t?: unknown })?.t;
    if (typeof tag !== "string") return;

    if (!KNOWN_TAGS.has(tag)) {
      // An unknown `t` is silently ignored (§3.1) — except the two frames
      // that carry a message, which are salvaged into a visible placeholder.
      const salvaged = salvage(tag, raw as Record<string, unknown>);
      if (salvaged) this.listener({ t: "event", event: salvaged });
      return;
    }

    const event = raw as ServerEvent;

    if (event.t === "ready") {
      this.connected = true;
      this.backoffMs = MIN_BACKOFF_MS; // only a session that reached ready resets the ladder
      // Status first — the sidebar stops saying offline before the refetch.
      this.listener({ t: "status", connected: true });
      this.listener({ t: "ready", user_id: event.user_id });
      return;
    }

    if (event.t === "error" && !this.connected) {
      // A frame with a reason is the gateway refusing the auth frame: terminal,
      // the loop ends, the caller decides what a dead JWT means. One without
      // has said nothing about the token: the socket's ten seconds ran out, or
      // the database could not be asked, on a gateway with too much in work
      // (docs/admission.md D1076). Signing out over that would end the sign-ins
      // of the people least able to get back in, and send them all to the
      // password form together. The gateway closes the socket after the frame,
      // and the ladder dials again.
      if (event.reason === undefined || event.reason === null) return;
      this.started = null;
      this.teardown();
      this.listener({ t: "auth_rejected", message: event.message, reason: event.reason });
      return;
    }

    this.listener({ t: "event", event });
  }

  private armKeepalive(gen: number): void {
    this.clearKeepalive();
    this.stuckKeepalives = 0;
    this.keepaliveTimer = setInterval(() => {
      if (gen !== this.generation || !this.ws) return;
      if (this.ws.readyState !== WebSocket.OPEN) return;
      // A black-holed route stops draining the send buffer: two keepalives
      // stuck in it is the web-reachable version of the 95 s deadline.
      if (this.ws.bufferedAmount > 0) {
        this.stuckKeepalives += 1;
        if (this.stuckKeepalives >= 2) {
          this.scheduleRetry("keepalives are not draining");
          return;
        }
      } else {
        this.stuckKeepalives = 0;
      }
      this.send({ t: "pong" });
    }, KEEPALIVE_MS);
  }

  private clearKeepalive(): void {
    if (this.keepaliveTimer !== null) {
      clearInterval(this.keepaliveTimer);
      this.keepaliveTimer = null;
    }
  }

  private scheduleRetry(_reason: string): void {
    this.teardown();
    const hadReachedReady = this.connected;
    this.setConnected(false);
    if (hadReachedReady) this.backoffMs = MIN_BACKOFF_MS;
    if (!this.started) return;
    // Drawn from the rung's upper half, the desktop's rule: every client of a
    // stand loses its socket at the same instant when the gateway restarts, and
    // a fixed ladder would bring them all back at the same instants too (D476).
    const wait = this.backoffMs - Math.random() * (this.backoffMs / 2);
    this.backoffMs = Math.min(this.backoffMs * 2, MAX_BACKOFF_MS);
    this.retryTimer = setTimeout(() => this.dial(), wait);
  }

  private setConnected(connected: boolean): void {
    if (this.connected === connected) return;
    this.connected = connected;
    this.listener({ t: "status", connected });
  }

  private teardown(): void {
    this.generation += 1;
    this.clearKeepalive();
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.ws) {
      const ws = this.ws;
      this.ws = null;
      ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
      try {
        ws.close();
      } catch {
        // Already closing: fine.
      }
    }
  }
}

/** The two frames worth a placeholder (chat-api.md §7): a message must not
 * vanish because this build cannot read it. `channel_id` and `seq` are what
 * makes it placeable; without them there is nowhere to put the notice. */
export function salvage(
  tag: string,
  raw: Record<string, unknown>,
): ServerEvent | null {
  if (tag !== "message" && tag !== "message_edited") return null;
  const channelId = asU64(raw["channel_id"]);
  const seq = asU64(raw["seq"]);
  if (channelId === null || seq === null) return null;
  const placeholder = {
    t: tag,
    channel_id: channelId,
    seq,
    sender_id: asU64(raw["sender_id"]) ?? 0,
    sent_at_ms: typeof raw["sent_at_ms"] === "number" ? raw["sent_at_ms"] : 0,
    nonce: typeof raw["nonce"] === "string" ? raw["nonce"] : "",
    text: "",
    entities: [],
    attachments: [],
    // The sentinel the feed keys its "this version cannot show this" row on:
    // a kind outside SERVICE_KINDS renders the unsupported sentence.
    service: { kind: "unknown" },
  };
  return placeholder as unknown as ServerEvent;
}

function asU64(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null;
}
